// src/screens/PrivacyPolicy.js
import React from "react";
import "../styles/LegalPage.css";

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-card">
        <h1>Pay2Pee Privacy Policy</h1>
        <p className="legal-meta">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <p>
          Welcome to <strong>Pay2Pee</strong>. Your privacy is extremely
          important to us. This Privacy Policy explains what information we
          collect, how we use it, and the choices you have.
        </p>

        <p>
          By using Pay2Pee, you agree to this Privacy Policy. If you do not
          agree, please do not use the app.
        </p>

        <h2>1. Who We Are</h2>
        <p>
          Pay2Pee is a platform that connects guests with host businesses that
          offer clean, paid restrooms.
        </p>
        <p>
          Website:{" "}
          <a
            href="https://pay2pee.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://pay2pee.app
          </a>
          <br />
          Email:{" "}
          <a href="mailto:support@pay2pee.app">support@pay2pee.app</a>
        </p>

        <h2>2. Information We Collect</h2>
        <h3>2.1 Information You Provide</h3>
        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Password (stored securely by Firebase; we never see it)</li>
          <li>Profile photo and optional details</li>
          <li>Restroom preferences</li>
          <li>Favorites, reviews, and ratings</li>
        </ul>

        <h3>2.2 Payment Information</h3>
        <p>
          Payments are processed by <strong>Stripe</strong>. We do not store
          your full payment card details or bank account numbers. Stripe
          securely handles:
        </p>
        <ul>
          <li>Card payments and tokens</li>
          <li>Guest charges</li>
          <li>Host payouts (Stripe Connect)</li>
        </ul>

        <h3>2.3 Location Information</h3>
        <p>
          With your permission, we collect your location to show nearby
          restrooms and improve map accuracy. We do not continuously track your
          location in the background.
        </p>

        <h3>2.4 Automatically Collected Information</h3>
        <p>We may automatically collect:</p>
        <ul>
          <li>Device type and operating system version</li>
          <li>App usage and performance data</li>
          <li>Anonymous analytics and crash reports</li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Create and manage your account</li>
          <li>Provide and improve search and booking features</li>
          <li>Process payments and host payouts</li>
          <li>Show your visit history and favorites</li>
          <li>Prevent fraud and abuse</li>
          <li>Send important updates and support messages</li>
          <li>Analyze and improve app performance</li>
        </ul>
        <p>
          We <strong>do not</strong> sell your personal information.
        </p>

        <h2>4. How Your Data Is Stored</h2>
        <p>We use trusted third-party services:</p>
        <ul>
          <li>
            <strong>Firebase Authentication</strong> for login and identity
            management.
          </li>
          <li>
            <strong>Firestore</strong> to store account data, visits,
            preferences, favorites, and host/location info.
          </li>
          <li>
            <strong>Stripe</strong> for payment processing and host payouts.
          </li>
          <li>
            <strong>Google Maps</strong> APIs for geolocation and map features.
          </li>
        </ul>
        <p>
          Data is protected with encryption in transit (HTTPS) and at rest where
          supported by these providers.
        </p>

        <h2>5. How We Share Information</h2>
        <p>
          We share data only with service providers that help us operate
          Pay2Pee:
        </p>
        <ul>
          <li>Stripe (payments and payouts)</li>
          <li>Firebase (auth and database)</li>
          <li>Google Maps (location and map rendering)</li>
          <li>Analytics and crash reporting tools (anonymous data)</li>
        </ul>
        <p>
          We may also share information if required by law or to protect our
          rights, users, or the public.
        </p>

        <h2>6. Your Choices and Rights</h2>
        <p>In the app, you can:</p>
        <ul>
          <li>Update your profile and preferences</li>
          <li>Reset your password</li>
          <li>Update your email address</li>
          <li>Manage favorites</li>
        </ul>
        <p>
          You may also request account and data deletion at any time by emailing{" "}
          <a href="mailto:support@pay2pee.app">support@pay2pee.app</a>.
        </p>

        <h2>7. Data Retention</h2>
        <p>
          We retain information for as long as necessary to provide the service,
          comply with legal obligations, resolve disputes, and enforce our
          agreements.
        </p>

        <h2>8. Children&apos;s Privacy</h2>
        <p>
          Pay2Pee is intended for users 18 years and older. We do not knowingly
          collect data from children. If you believe a child has used the app,
          contact us so we can delete the account.
        </p>

        <h2>9. International Users (GDPR / CCPA)</h2>
        <p>
          If you are located in the EU, UK, or California, you may have
          additional rights, such as:
        </p>
        <ul>
          <li>Accessing the data we hold about you</li>
          <li>Correcting inaccurate information</li>
          <li>Requesting deletion of your data</li>
          <li>Objecting to certain types of processing</li>
          <li>Requesting a copy of your data (portability)</li>
        </ul>
        <p>
          To exercise these rights, email{" "}
          <a href="mailto:support@pay2pee.app">support@pay2pee.app</a>.
        </p>

        <h2>10. Security</h2>
        <p>
          We use industry-standard security practices, but no system is
          completely secure. We work continuously to protect your information.
        </p>

        <h2>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will update
          the &quot;Last updated&quot; date and may notify you in the app if
          changes are significant.
        </p>

        <h2>12. Contact Us</h2>
        <p>
          For questions, concerns, or requests related to privacy, contact us
          at:
        </p>
        <p>
          Email: <a href="mailto:support@pay2pee.app">support@pay2pee.app</a>
          <br />
          Website:{" "}
          <a
            href="https://pay2pee.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://pay2pee.app
          </a>
        </p>
      </div>
    </div>
  );
}
